// libs
import React, { memo, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import StickyBox from 'react-sticky-box';
import { useSelector } from 'react-redux';
// styles
import {
  AllBrands,
  AllBrandsHeading,
  AvailableInitials,
  BrandInitial,
  ClearButton,
  IconSearchWrapper,
  Input,
  InputContainer,
  MenuItem,
} from './styles';

// helpers
import { generateTrackingURL, getMenuContent, sorter } from './helpers';

// common helpers for all brands
import {
  getBucketizedBrands,
  getFilteredBrands,
} from 'shared/layouts/MegaMenuV2/components/Brands/helpers';
import { ALL_BRANDS, META_INFO, PLACEHOLDER } from './constants';

// components
import Loader from 'shared/components/Loader';
import MetaInfo from 'shared/components/AdPlatformWidgets/components/MetaInfo';

// constants
import { COLORS } from 'shared/styles/base';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

//hooks
import { useDomainConfig } from 'shared/domainConfig/context/context';

// selectors
import { getStoreId } from 'shared/store/app/selectors';

const CHEVRON_RIGHT_ICON_SIZE = '20px';
const SEARCH_ICON_SIZE = '20px';

function AllBrandsLayout() {
  const ChevronRightIcon = useStoreIcon(IconName.CHEVRON_RIGHT_SMALL);
  const SearchIcon = useStoreIcon(IconName.SEARCH);
  const CircleXFilled = useStoreIcon(IconName.CIRCLE_X_FILLED);
  const storeId = useSelector(getStoreId);
  const [apiResponse, setApiResponse] = useState({});
  const { ALL_BRANDS_DEFAULT_SELECTION } = useDomainConfig();
  const [selected, setSelected] = useState(ALL_BRANDS_DEFAULT_SELECTION);
  const [searchTerm, setSearchTerm] = useState('');

  const categories = get(apiResponse, 'children_data', []);
  const filerted = categories.filter((item) => {
    return item.name === selected;
  });

  const handleClick = (value: string) => {
    setSelected(value);
  };

  useEffect(() => {
    getMenuContent(storeId).then((data) => {
      setApiResponse(data);
    });
  }, [storeId]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const bucketizedBrands = useMemo(() => {
    const fileteredData = get(filerted, '[0].children_data', []);
    if (isEmpty(searchTerm)) return getBucketizedBrands(fileteredData);

    return getBucketizedBrands(getFilteredBrands(fileteredData, searchTerm));
  }, [filerted, searchTerm]);

  const availableInitials = useMemo(() => {
    return Object.keys(bucketizedBrands).sort(sorter);
  }, [bucketizedBrands]);

  if (categories.length === 0) {
    return (
      <>
        <MetaInfo metaData={META_INFO} />
        <AllBrands>
          <div className="content-wrapper">
            <Loader />
          </div>
        </AllBrands>
      </>
    );
  }

  return (
    <>
      <MetaInfo metaData={META_INFO} />
      <AllBrands data-at="mega-menu">
        <div className="content-wrapper">
          <div className="left-section">
            <StickyBox offsetTop={0} offsetBottom={20}>
              <MenuItem
                selected={selected === ALL_BRANDS}
                onClick={() => {
                  handleClick(ALL_BRANDS);
                }}
              >
                <AllBrandsHeading>
                  {ALL_BRANDS}
                  <ChevronRightIcon size={CHEVRON_RIGHT_ICON_SIZE} />
                </AllBrandsHeading>
              </MenuItem>
              <AvailableInitials>
                {categories.map((item, idx: number) => {
                  if (item.name && item.name !== ALL_BRANDS)
                    return (
                      <MenuItem
                        className="menu-item"
                        selected={selected === item.name}
                        onClick={() => {
                          handleClick(item.name);
                        }}
                        key={`category-${item.name || idx}`}
                      >
                        <div className="content">
                          {item.name}
                          <i className="icon-arrow-next" />
                        </div>
                      </MenuItem>
                    );
                })}
              </AvailableInitials>
            </StickyBox>
          </div>

          <div className="right-section">
            <InputContainer>
              <Input
                onChange={(e) => handleSearch(e)}
                label={PLACEHOLDER}
                value={searchTerm}
                leadingComponent={
                  <IconSearchWrapper>
                    <SearchIcon size={SEARCH_ICON_SIZE} color={COLORS.BLACK_36} />
                  </IconSearchWrapper>
                }
                suffixComponent={
                  searchTerm.length > 0 && (
                    <ClearButton
                      onClick={() => setSearchTerm('')}
                      aria-label={'Clear'}
                      data-test="headersearch-clear-btn"
                    >
                      <CircleXFilled />
                    </ClearButton>
                  )
                }
              />
            </InputContainer>

            {availableInitials.map((item, index) => {
              return (
                <AvailableInitials key={`brand-initials-${item}-${index}`}>
                  <BrandInitial>{item}</BrandInitial>
                  {bucketizedBrands[item].map((brand: any, idx: number) => {
                    if (brand.name === '') {
                      return null;
                    }

                    return (
                      <MenuItem key={`brand-${brand.id || idx}`} className="menu-item">
                        <a
                          href={generateTrackingURL({
                            ...brand,
                            position: idx + 1,
                          })}
                          className="right-content"
                        >
                          {brand.name}
                          <ChevronRightIcon size={CHEVRON_RIGHT_ICON_SIZE} />
                        </a>
                      </MenuItem>
                    );
                  })}
                </AvailableInitials>
              );
            })}
          </div>
        </div>
      </AllBrands>
    </>
  );
}

export default memo(AllBrandsLayout);
